import { useState, useEffect, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MetalSheetRollingMachine from '../assets/images/products/Metal Sheet Rolling Machine.png';
import MetalShearingMachine from '../assets/images/products/Metal Shearing Machine.png';
import LaserCuttingMachine from '../assets/images/products/Laser Cutting Machine.png';
import LaserWeldingMachine from '../assets/images/products/Laser Welding Machine.png';
import VacuumOven from '../assets/images/products/Vacuum Ovens.png';
import PanelBoards from '../assets/images/products/Panel Boards.png';
import SprayPaintingBooths from '../assets/images/products/Spray Painting Booths.png';
import RMU from '../assets/images/products/RMU (Ring Main Unit).png';

const categories = [
  "All",
  "Metal Machines",
  "Laser Technology",
  "Electrical",
  "Industrial Equipment",
];

const productList = [
  {
    name: "Metal Sheet Rolling Machine",
    category: "Metal Machines",
    img: MetalSheetRollingMachine,
  },
  {
    name: "Metal Shearing Machine",
    category: "Metal Machines",
    img: MetalShearingMachine,
  },
  {
    name: "Laser Cutting Machine",
    category: "Laser Technology",
    img: LaserCuttingMachine,
  },
  {
    name: "Laser Welding Machine",
    category: "Laser Technology",
    img: LaserWeldingMachine,
  },
  {
    name: "Vacuum Ovens",
    category: "Industrial Equipment",
    img: VacuumOven,
  },
  {
    name: "Panel Boards",
    category: "Electrical",
    img: PanelBoards,
  },
  {
    name: "Spray Painting Booths",
    category: "Industrial Equipment",
    img: SprayPaintingBooths,
  },
  {
    name: "RMU (Ring Main Unit)",
    category: "Electrical",
    img: RMU,
  },
];

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [popupProduct, setPopupProduct] = useState<any>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // simulate API delay
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const filteredProducts =
    selectedCategory === "All"
      ? productList
      : productList.filter((p) => p.category === selectedCategory);

  const handleOpenPopup = (product: any) => {
    setPopupProduct(product);
    setMessage(`I am interested in ${product.name}. Please provide more details.`);
  };

  const handleClosePopup = () => {
    setPopupProduct(null);
    setName("");
    setMessage("");
    setPhone("");
    setEmail("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    formDataToSend.append('name', name);
    formDataToSend.append('email', email);
    formDataToSend.append('phone', phone);
    formDataToSend.append('message', message);
    formDataToSend.append('subject', `Enquiry for ${popupProduct.name}`);

    fetch('/submit_form.php', {
      method: 'POST',
      body: formDataToSend,
    })
    .then(response => response.text())
    .then(data => {
      alert(data); // Display the response from the PHP script
      if (data.includes("success")) { // Check for a success message from PHP
        handleClosePopup();
      }
    })
    .catch(error => {
      console.error('Error:', error);
      alert('An error occurred. Please try again later.');
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 relative">
      {/* Header with Category Filter (mobile only) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-3xl font-bold text-blue-900">Our Products</h1>
        <button
          className="md:hidden mt-4 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition"
          onClick={() => setFilterOpen(true)}
        >
          Category Filter
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-8">
        {/* Left Sidebar (Desktop/Tablet only) */}
        <div className="hidden md:block bg-gray-50 p-6 rounded-xl shadow h-fit">
          <h2 className="text-xl font-bold text-blue-900 mb-4">Categories</h2>
          <ul className="space-y-3">
            {categories.map((cat, index) => (
              <li
                key={index}
                onClick={() => setSelectedCategory(cat)}
                className={`cursor-pointer px-3 py-2 rounded-lg transition ${
                  selectedCategory === cat
                    ? "bg-blue-600 text-white font-semibold"
                    : "hover:bg-blue-100 text-gray-700"
                }`}
              >
                {cat}
              </li>
            ))}
          </ul>
        </div>

        {/* Products Grid */}
        <div className="md:col-span-3">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
            {loading
              ? Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-xl shadow p-4 text-center"
                    >
                      <Skeleton height={120} className="mb-3" />
                      <Skeleton height={20} width={150} className="mb-2 mx-auto" />
                      <Skeleton height={35} width={100} className="mx-auto" />
                    </div>
                  ))
              : filteredProducts.map((product, index) => (
                    <motion.div
                      key={index}
                      className="bg-white rounded-xl shadow hover:shadow-lg transition overflow-hidden"
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <img
                        src={product.img}
                        alt={product.name}
                        className="w-full h-36 object-cover"
                      />
                      <div className="p-4 text-center">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-800 line-clamp-2">
                          {product.name}
                        </h3>
                        <button
                          onClick={() => handleOpenPopup(product)}
                          className="mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition text-sm"
                        >
                          Enquire Now
                        </button>
                      </div>
                    </motion.div>
                ))}
          </div>
        </div>
      </div>

      {/* Mobile Filter Bottom Sheet */}
      <AnimatePresence>
        {filterOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-end z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white w-full rounded-t-2xl p-6"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Filter Categories</h2>
                <button
                  onClick={() => setFilterOpen(false)}
                  className="text-gray-500 hover:text-red-500"
                >
                  ✖
                </button>
              </div>
              <ul className="space-y-3">
                {categories.map((cat, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setFilterOpen(false);
                    }}
                    className={`cursor-pointer px-3 py-2 rounded-lg transition ${
                      selectedCategory === cat
                        ? "bg-blue-600 text-white font-semibold"
                        : "hover:bg-blue-100 text-gray-700"
                    }`}
                  >
                    {cat}
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popup Modal for Product Enquiry */}
      <AnimatePresence>
        {popupProduct && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg relative"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <button
                onClick={handleClosePopup}
                className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
              >
                ✖
              </button>
              <h2 className="text-xl font-bold text-blue-900 mb-4">
                Enquiry for {popupProduct.name}
              </h2>
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none p-3 mb-3 "
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <textarea
                  className="w-full border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none p-3 mb-3"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Phone Number"
                  className="w-full border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none p-3 mb-3"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none p-3 mb-3"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Products;